@import "@/scss/use-bootstrap-mixins.scss";

$muted: #70797e;
$ink: #37464f;
$surface: #f5f5f5;

.triggerButton {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  border: none;
  border-radius: 10px;
  padding: 10px 16px;
  background: $primary;
  color: $textColorOnPrimary;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  text-align: left;

  &:focus-visible {
    outline: 2px solid $primary;
    outline-offset: 2px;
  }
}

.triggerPrompt {
  font-size: 12px;
  font-weight: 400;
}

.overlay {
  position: fixed;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  overflow-y: auto;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999999999; // Matches the details modal, which must sit above host-page chrome.

  @include media-breakpoint-up(lg) {
    align-items: center;
    padding: 16px;
  }
}

.dialog {
  position: relative;
  display: flex;
  flex-direction: column-reverse;
  width: 100%;
  overflow: hidden;
  border-radius: 0 0 15px 15px;
  background: #fff;
  font-family: 'Poppins', sans-serif;

  @include media-breakpoint-up(lg) {
    flex-direction: row;
    width: 830px;
    border-radius: 15px;
  }
}

.closeButton {
  position: absolute;
  top: 15px;
  right: 15px;
  z-index: 10;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: $surface;
  color: $muted;
  font-size: 23px;
  line-height: 1;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: $muted;
    color: #fff;
  }
}

.helpPanel {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 20px;
  background: $primary;
  color: $textColorOnPrimary;
  text-align: center;

  @include media-breakpoint-up(lg) {
    flex: 0 0 280px;
    width: 280px;
    padding: 40px;
  }
}

.helpTitle {
  margin: 10px 0;
  font-size: 24px;
  font-weight: 600;
}

.helpSubtitle {
  font-size: 16px;
}

.helpPanel .supportIcon {
  width: 70px;
  height: 70px;
  margin-bottom: 16px;
}

.findSpaceButton {
  width: 220px;
  margin-top: 20px;
  margin-bottom: 15px;
  padding: 10px 15px;
  border: 3px solid currentColor;
  border-radius: 10px;
  background: transparent;
  color: inherit;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #fff;
    border-color: #fff;
    color: $primary;
  }

  @include media-breakpoint-up(lg) {
    width: 100%;
    margin-top: 40px;
  }
}

.phone {
  font-weight: 600;
}

@mixin back-chevron {
  svg {
    width: 9px;
    height: 16px;
    transition: margin-right 0.3s ease-in-out;
  }

  path {
    stroke: currentColor;
  }
}

.previousStep {
  @include back-chevron;
  display: none;
  position: absolute;
  bottom: 50px;
  align-items: center;
  gap: 15px;
  border: none;
  background: transparent;
  color: inherit;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;

  &:hover svg {
    margin-right: 10px;
  }

  @include media-breakpoint-up(lg) {
    display: flex;
  }
}

.mainPanel {
  position: relative;
  flex-grow: 1;
  width: 100%;
  padding: 20px;
  text-align: center;

  @include media-breakpoint-up(lg) {
    padding: 40px;
    text-align: left;
  }
}

.previousStepCompact {
  @include back-chevron;
  position: absolute;
  top: 15px;
  left: 15px;
  padding: 8px;
  border: none;
  background: transparent;
  color: $primary;
  cursor: pointer;

  svg {
    width: 14px;
    height: 24px;
  }

  @include media-breakpoint-up(lg) {
    display: none;
  }
}

.headings {
  margin-bottom: 25px;
  padding: 0 48px;

  @include media-breakpoint-up(lg) {
    padding: 0 48px 0 0;
  }
}

.title {
  color: $primary;
  font-size: 29px;
  font-weight: 600;

  &:focus {
    outline: none;
  }
}

.intro {
  margin: 10px 0 0;
  color: $muted;
  font-size: 16px;
}

.stepRow {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}

.stepText {
  display: inline-block;
  margin: 0 0 20px;
  font-size: 18px;
  font-weight: 500;

  span {
    font-weight: 600;
  }
}

.helperText {
  padding: 0;
  color: $muted;
  font-size: 13px;
  font-style: italic;
  font-weight: 400;

  .stepRow & {
    display: none;

    @include media-breakpoint-up(lg) {
      display: inline;
    }
  }
}

.withArrow::after {
  content: "\2191";
  display: inline-block;
  margin-left: 7px;
  font-style: normal;
}

.modeOptions {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
}

.modeOption {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  width: 48%;
  margin-bottom: 15px;
  padding: 10px 15px;
  border: none;
  border-radius: 10px;
  background-color: $surface;
  color: $primary;
  cursor: pointer;
  transition: all 0.3s ease;

  .modeIcon {
    display: inline-flex;
    color: $ink;

    svg {
      width: 30px;
      height: 30px;
    }
  }

  .modeLabel {
    color: $muted;
    font-size: 14px;
    font-weight: 500;
  }

  &:hover:not(:disabled),
  &:focus-visible {
    background-color: $primary;
    outline: none;

    .modeIcon,
    .modeLabel {
      color: $textColorOnPrimary;
    }
  }

  &:disabled {
    cursor: not-allowed;
  }
}

.orDivider {
  margin: 40px 0 20px;
  font-size: 24px;
  font-weight: 600;
}

.field {
  margin-bottom: 40px;

  @include media-breakpoint-up(lg) {
    margin-bottom: 30px;
  }
}

.fieldLabel {
  display: block;
  font-weight: 600;
}

// The thumb's centre travels from half a thumb in from each end, not across the whole track.
@mixin at-thumb-centre($property) {
  #{$property}: calc(var(--thumb-size) / 2 + (100% - var(--thumb-size)) * var(--slider-fraction));
}

.slider {
  --thumb-size: 32px;
  position: relative;
  margin: 35px 0 30px;

  input[type="range"] {
    display: block;
    width: 100%;
    height: 10px;
    margin: 0;
    border-radius: 10px;
    --slider-fill: calc(var(--thumb-size) / 2 + (100% - var(--thumb-size)) * var(--slider-fraction));
    background: linear-gradient(
      to right,
      $primary var(--slider-fill),
      #d8e0f3 var(--slider-fill)
    );
    appearance: none;
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid $primary;
      outline-offset: 4px;
    }

    &::-webkit-slider-thumb {
      box-sizing: border-box;
      width: var(--thumb-size);
      height: var(--thumb-size);
      border: 3px solid #fff;
      border-radius: 50%;
      background: $primary;
      appearance: none;
    }

    &::-moz-range-thumb {
      box-sizing: border-box;
      width: var(--thumb-size);
      height: var(--thumb-size);
      border: 3px solid #fff;
      border-radius: 50%;
      background: $primary;
    }
  }
}

.sliderValue {
  position: absolute;
  bottom: calc(100% + 8px);
  @include at-thumb-centre(left);
  transform: translateX(-50%);
  font-weight: 600;
}

.sliderTicks {
  position: relative;
  height: 20px;
  margin-top: 8px;
  color: $muted;
  font-size: 13px;

  span {
    position: absolute;
    @include at-thumb-centre(left);
    transform: translateX(-50%);
  }
}

.submitRow {
  margin-top: 40px;
}

.submitButton {
  padding: 10px 24px;
  border: 1px solid $primary;
  border-radius: 10px;
  background: $primary;
  color: $textColorOnPrimary;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid $primary;
    outline-offset: 2px;
  }
}
